import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { applySrsRating, isDueToday } from '../lib/srs'

// Review session manager for the /english/review surface.
//
// The hook fetches every due card on mount, holds them in a local
// queue, and exposes:
//   - `current`    — the card at the head of the queue (or null when done)
//   - `remaining`  — count still to review (including the current one)
//   - `done`       — count already reviewed this session
//   - `rate(n)`    — apply rating 1-4, advance queue, write to DB
//   - `replayAgain`— after finishing, re-queue any cards that were rated
//                    Again so the user can keep drilling the lapses
//
// Each `rate` call:
//   1. Applies SM-2 locally and updates the row in memory so the UI
//      can advance without waiting on the network.
//   2. Fires the english_vocab UPDATE + english_review_log INSERT in
//      parallel (best-effort; UI doesn't block on either).
//   3. If the card was rated Again, pushes a clone back onto the
//      tail so the user re-sees lapses during the same session.
//
// On finish (queue empty), if at least one card was reviewed, we
// upsert an `english_study_sessions` row for today summarising the
// session — this is what /english (dashboard) shows as "today's
// reviews" and what the weekly-insights cron will read in PR H.
export function useEnglishReview() {
  const { user } = useAuth()
  const [queue, setQueue] = useState([])
  const [done, setDone] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const sessionStartRef = useRef(null)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    setError(null)
    const today = new Date().toISOString().slice(0, 10)
    const { data, error: err } = await supabase
      .from('english_vocab')
      .select('*')
      .neq('srs_state', 'suspended')
      .or(`srs_due_date.lte.${today},srs_due_date.is.null`)
      .order('srs_due_date', { ascending: true, nullsFirst: true })
    if (err) {
      setError(err.message)
      setLoading(false)
      return
    }
    const due = (data || []).filter((c) => isDueToday(c, today))
    setQueue(due)
    setDone(0)
    sessionStartRef.current = Date.now()
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  const current = queue[0] || null
  const remaining = queue.length

  const rate = useCallback(async (rating) => {
    if (!current || !user) return
    const result = applySrsRating(current, rating)
    // Optimistic queue advance — drop the head and (if Again) re-queue
    // a copy of the card with the new SRS state so the user re-sees it.
    setQueue((prev) => {
      const next = prev.slice(1)
      if (rating === 1) {
        next.push({ ...current, ...result.patch })
      }
      return next
    })
    setDone((d) => d + 1)
    // Fire writes in parallel — neither blocks the UI advance.
    Promise.all([
      supabase
        .from('english_vocab')
        .update(result.patch)
        .eq('id', current.id),
      supabase
        .from('english_review_log')
        .insert({
          user_id: user.id,
          vocab_id: current.id,
          rating,
          prev_interval_days: result.log.prev_interval_days,
          new_interval_days: result.log.new_interval_days,
          reviewed_at: result.log.reviewed_at,
        }),
    ]).catch(() => { /* best-effort */ })
  }, [current, user])

  // When the queue empties, fold a study-session row in for today so
  // the dashboard + weekly insights can attribute the review minutes.
  // Idempotent enough — if the user re-opens /english/review the same
  // day, we'll insert a second row rather than amend; that's fine,
  // both rows aggregate correctly.
  const finishedRef = useRef(false)
  useEffect(() => {
    if (loading) return
    if (queue.length > 0) return
    if (done === 0) return
    if (finishedRef.current) return
    finishedRef.current = true
    const durationMin = sessionStartRef.current
      ? Math.max(1, Math.round((Date.now() - sessionStartRef.current) / 60000))
      : null
    supabase
      .from('english_study_sessions')
      .insert({
        user_id: user.id,
        session_date: new Date().toISOString().slice(0, 10),
        duration_minutes: durationMin,
        vocab_reviewed: done,
        notes: null,
      })
      .then(() => { /* no-op */ })
      .catch(() => { /* best-effort */ })
  }, [loading, queue.length, done, user])

  return {
    current,
    remaining,
    done,
    loading,
    error,
    rate,
    refetch: fetch,
  }
}

// Lightweight "how many cards are due right now?" probe for the
// dashboard tile. Avoids loading the full row payload — just counts.
export function useEnglishDueCount() {
  const { user } = useAuth()
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return () => { alive = false }
    }
    const today = new Date().toISOString().slice(0, 10)
    supabase
      .from('english_vocab')
      .select('id', { count: 'exact', head: true })
      .neq('srs_state', 'suspended')
      .or(`srs_due_date.lte.${today},srs_due_date.is.null`)
      .then(({ count: c, error }) => {
        if (!alive) return
        if (!error) setCount(c || 0)
        setLoading(false)
      })
    return () => { alive = false }
  }, [user])

  return { count, loading }
}
